import { Box } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type { GridApi, GridRowId } from '@mui/x-data-grid-premium';
import type { UpcomingInvoicesReportItem } from '@vy/types';
import { sumBy } from '@vy/utils';
import { useCallback, useMemo, useState } from 'react';
import type { MutableRefObject } from 'react';
import { EmptyTableOverlay } from '../../../../../components/design/EmptyTableOverlay';
import {
  hideColumnSeparatorsSx,
  suppressPinnedColumnSeparatorSx,
} from '../../../../../components/new-design/table/vy-table/pinned-columns-sx';
import { LoadingOverlay } from '../../../../../components/new-design/table/vy-table/VyTable';
import { TOTALS_ROW_ID } from '../../../reports-components/report-table-consts';
import {
  BILLING_FORECAST_REPORT_KEYS,
  useBillingForecastReportColumns,
  useBillingForecastDateHeaders,
} from './BillingForecastReportTableColumns';
import { BillingForecastInvoiceDetailPanel } from './BillingForecastInvoiceDetailPanel';
import type {
  BillingForecastSummaryRow,
  BillingForecastBreakdownInvoice,
} from './billing-forecast-report-aggregator';

const PINNED_COLUMNS = {
  left: ['__detail_panel_toggle__', BILLING_FORECAST_REPORT_KEYS.CUSTOMER_NAME],
  right: [BILLING_FORECAST_REPORT_KEYS.TOTAL],
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 500];

type BillingForecastReportTableProps = {
  summaryRows: BillingForecastSummaryRow[];
  breakdownByCustomerId: Map<string, BillingForecastBreakdownInvoice[]>;
  reportItems: UpcomingInvoicesReportItem[];
  fromDate?: Date | null;
  toDate?: Date | null;
  apiRef?: MutableRefObject<GridApi>;
  isLoading?: boolean;
};

export const BillingForecastReportTable: React.FC<BillingForecastReportTableProps> = ({
  summaryRows,
  breakdownByCustomerId,
  reportItems,
  fromDate,
  toDate,
  isLoading,
  apiRef,
}) => {
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(new Set());

  const monthHeaders = useBillingForecastDateHeaders(reportItems, fromDate, toDate);
  const columns = useBillingForecastReportColumns(monthHeaders);

  const totalsRow = useMemo((): BillingForecastSummaryRow => {
    const monthlyTotals = new Map<string, number>();

    for (const monthKey of monthHeaders) {
      const monthTotal = sumBy(
        summaryRows,
        (row) => row.monthlyTotals.get(monthKey) ?? 0,
      );
      monthlyTotals.set(monthKey, monthTotal);
    }

    const grandTotal = sumBy(summaryRows, (row) => row.total);

    return {
      customer: { id: TOTALS_ROW_ID, name: 'Total' },
      currency: '',
      monthlyTotals,
      total: grandTotal,
    };
  }, [summaryRows, monthHeaders]);

  const pinnedRows = useMemo(
    () => ({ bottom: [totalsRow] }),
    [totalsRow],
  );

  const getDetailPanelContent = useCallback(
    (params: { row: BillingForecastSummaryRow }) => {
      const customerInvoices = breakdownByCustomerId.get(params.row.customer.id) ?? [];

      if (customerInvoices.length === 0) return null;

      return (
        <BillingForecastInvoiceDetailPanel
          invoices={customerInvoices}
          monthHeaders={monthHeaders}
        />
      );
    },
    [breakdownByCustomerId, monthHeaders],
  );

  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const getRowId = useCallback(
    (row: BillingForecastSummaryRow) => row.customer.id,
    [],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '& .MuiDataGrid-pinnedRows--bottom': {
          marginTop: '-1px',
        },
      }}
    >
      <DataGridPremium
        apiRef={apiRef}
        rows={summaryRows}
        columns={columns}
        getRowId={getRowId}
        loading={isLoading}
        pinnedRows={pinnedRows}
        pinnedColumns={PINNED_COLUMNS}
        density="compact"
        pagination
        detailPanelExpandedRowIds={expandedRowIds}
        onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        sx={{
          height: '100%',
          border: 'none',
          borderTop: 1,
          borderColor: 'divider',
          borderRadius: 0,
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },
          '& .MuiDataGrid-pinnedRows': {
            boxShadow: 'none !important',
            backgroundColor: 'common.white',
          },
          '& .MuiDataGrid-pinnedRows--bottom': {
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'common.white',
          },
          ...suppressPinnedColumnSeparatorSx,
          ...hideColumnSeparatorsSx,
          '& .MuiDataGrid-detailPanel': {
            overflow: 'visible',
          },
        }}
        slots={{
          noRowsOverlay: EmptyTableOverlay,
          loadingOverlay: LoadingOverlay,
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />
    </Box>
  );
};
